import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { API } from "@/assets";

const SiteAuthCtx = createContext({ user: undefined, profile: null, setUser: () => {}, signOut: async () => {}, refreshProfile: async () => {} });

export const useSiteAuth = () => useContext(SiteAuthCtx);

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
export const signInWithGoogle = () => {
  const redirectUrl = window.location.origin + "/account";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
};

// map FastAPI 422 detail arrays to a readable string
export const fmtAuthError = (detail) => {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || "").filter(Boolean).join(" ");
  return String(detail);
};

export const SiteAuthProvider = ({ children }) => {
  const [user, setUser] = useState(undefined); // undefined = checking, null = signed out
  const [profile, setProfile] = useState(null); // Medusa player profile (XP/level)

  const refreshProfile = useCallback(async () => {
    try {
      const r = await fetch(`${API}/players/me`, { credentials: "include" });
      if (r.ok) {
        const d = await r.json();
        setUser(d.user);
        setProfile(d.profile);
        return d.profile;
      }
    } catch { /* offline */ }
    setProfile(null);
    return null;
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check —
    // AuthCallback exchanges the session_id and establishes the session first.
    if (window.location.hash?.includes("session_id=")) return;
    fetch(`${API}/auth/google/me`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : null))
      .then((u) => {
        setUser(u);
        if (u) refreshProfile();
      })
      .catch(() => setUser(null));
  }, [refreshProfile]);

  // any XP grant anywhere in the app refreshes the shared profile (PlayerCard updates live)
  useEffect(() => {
    const onXp = () => refreshProfile();
    window.addEventListener("medusa:xp", onXp);
    return () => window.removeEventListener("medusa:xp", onXp);
  }, [refreshProfile]);

  const signOut = useCallback(async () => {
    await fetch(`${API}/auth/google/logout`, { method: "POST", credentials: "include" });
    setUser(null);
    setProfile(null);
  }, []);

  // email/password player auth (same session cookie as Google)
  const signInWithPassword = useCallback(async (email, password) => {
    const r = await fetch(`${API}/players/login`, {
      method: "POST", credentials: "include", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email, password }),
    });
    const d = await r.json().catch(() => ({}));
    if (!r.ok) throw new Error(fmtAuthError(d.detail));
    setUser(d.user); setProfile(d.profile);
    return d;
  }, []);

  const registerPlayer = useCallback(async (name, email, password, ageConfirmed) => {
    const r = await fetch(`${API}/players/register`, {
      method: "POST", credentials: "include", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ name, email, password, age_confirmed: ageConfirmed }),
    });
    const d = await r.json().catch(() => ({}));
    if (!r.ok) throw new Error(fmtAuthError(d.detail));
    setUser(d.user); setProfile(d.profile);
    return d;
  }, []);

  return (
    <SiteAuthCtx.Provider value={{ user, profile, setUser, signOut, refreshProfile, signInWithPassword, registerPlayer }}>
      {children}
    </SiteAuthCtx.Provider>
  );
};
